import { useState, useEffect } from 'react'
import { 
  User, 
  Mail, 
  Phone, 
  FileText, 
  Calendar,
  DollarSign,
  Send,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react'
import { apiRequest } from '@/config/api'
import ApplicationStatus from './ApplicationStatus'
import LocationAutocomplete from '@/components/common/LocationAutocomplete'
import ResumeUploadWidget from './ResumeUploadWidget'
import ResumeSelector from '@/components/common/ResumeSelector'
import { useUserProfile } from '@/contexts/UserContext'

interface PublicPosition {
  uuid: string
  title: string
  company_name: string
}

interface ApplicationFormProps {
  position: PublicPosition
  onSuccess: () => void
  authenticatedEmail?: string
}

export default function ApplicationForm({ position, onSuccess, authenticatedEmail }: ApplicationFormProps) {
  // Try to get user profile, but don't fail if UserProvider is not available
  let userProfile: any = null
  try {
    userProfile = useUserProfile()
  } catch (error) {
    // UserProvider not available or no user logged in - continue without profile
    userProfile = null
  }

  // Support both camelCase and snake_case keys returned by the API/user context
  const profileFirstName = userProfile?.firstName || userProfile?.first_name || ''
  const profileLastName = userProfile?.lastName || userProfile?.last_name || ''
  const shouldLockFirstName = Boolean(userProfile && profileFirstName)
  const shouldLockLastName = Boolean(userProfile && profileLastName)

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: authenticatedEmail || '',
    phone: '',
    current_title: '',
    current_company: '',
    current_location: '',
    total_experience_years: '',
    expected_salary: '',
    expected_salary_currency: 'USD',
    skills: [],
    linkedin_url: '',
    portfolio_url: '',
    resume_url: '',
    summary: '',
    cover_letter: '',
    available_start_date: '',
    data_consent_given: false
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [success, setSuccess] = useState(false)
  const [needsOtp, setNeedsOtp] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [showApplicationStatus, setShowApplicationStatus] = useState(false)
  const [existingApplicationId, setExistingApplicationId] = useState<number | null>(null)
  const [resumeParseError, setResumeParseError] = useState('')
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null)
  const [showResumeUpdate, setShowResumeUpdate] = useState(false)
  const [isUpdatingResume, setIsUpdatingResume] = useState(false)

  // Pre-populate form data from user profile
  useEffect(() => {
    if (!userProfile) return

    setFormData(prev => ({
      ...prev,
      first_name: prev.first_name || profileFirstName,
      last_name: prev.last_name || profileLastName,
      email: prev.email || authenticatedEmail || userProfile.email || '',
      phone: prev.phone || userProfile.phone || '',
      current_title: prev.current_title || userProfile.title || '',
      current_location: prev.current_location || userProfile.location || '',
      // Keep other fields as they are to avoid overriding user input
    }))
  }, [userProfile, authenticatedEmail, profileFirstName, profileLastName])

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
    // Clear field-specific error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const getFieldError = (fieldName: string) => {
    return fieldErrors[fieldName]
  }

  const hasFieldError = (fieldName: string) => {
    return !!fieldErrors[fieldName]
  }

  const handleResumeDataParsed = (parsedData: any) => {
    setResumeParseError('')
    
    // Auto-fill form data with parsed resume information
    const updates: Partial<typeof formData> = {}
    
    if (parsedData.email && !formData.email) {
      updates.email = parsedData.email
    }
    
    if (parsedData.phone && !formData.phone) {
      updates.phone = parsedData.phone
    }
    
    if (parsedData.current_title && !formData.current_title) {
      updates.current_title = parsedData.current_title
    }
    
    if (parsedData.current_company && !formData.current_company) {
      updates.current_company = parsedData.current_company
    }
    
    if (parsedData.summary && !formData.summary) {
      updates.summary = parsedData.summary
    }
    
    if (parsedData.linkedin_url && !formData.linkedin_url) {
      updates.linkedin_url = parsedData.linkedin_url
    }
    
    if (parsedData.skills && Array.isArray(parsedData.skills) && parsedData.skills.length > 0) {
      updates.skills = parsedData.skills
    }
    
    // Apply all updates at once
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }))
    }
  }

  const handleResumeParseError = (error: string) => {
    setResumeParseError(error)
  }

  const handleResumeSelect = (resumeId: string | null, resumeUrl?: string) => {
    setSelectedResumeId(resumeId)
    if (resumeUrl) {
      setFormData(prev => ({ ...prev, resume_url: resumeUrl }))
    }
  }

  const handleUpdateResume = async () => {
    if (!selectedResumeId || !existingApplicationId) {
      setError('Please select a resume to update your application')
      return
    }

    setIsUpdatingResume(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const response = await apiRequest(`applicants/application/${existingApplicationId}/resume`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          resume_id: selectedResumeId
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setSuccess(true)
        setTimeout(() => {
          onSuccess()
        }, 2000)
      } else {
        setError(data.error || data.message || 'Failed to update resume')
      }
    } catch (err: any) {
      setError('Failed to update resume. Please try again.')
    } finally {
      setIsUpdatingResume(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Basic validation
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return
    }
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }
    if (!selectedResumeId) {
      setError('Please select a resume for your application')
      return
    }
    if (!formData.data_consent_given) {
      setError('Please provide consent to proceed')
      return
    }

    setSubmitting(true)
    setError('')
    setFieldErrors({})

    try {
      // Convert empty numeric fields to null to avoid validation errors
      const processedFormData = {
        ...formData,
        total_experience_years: formData.total_experience_years === '' ? null : 
          isNaN(parseFloat(formData.total_experience_years)) ? formData.total_experience_years : parseFloat(formData.total_experience_years),
        expected_salary: formData.expected_salary === '' ? null : 
          isNaN(parseFloat(formData.expected_salary)) ? formData.expected_salary : parseFloat(formData.expected_salary)
      }

      const requestData = {
        position_uuid: position.uuid, // Use the position UUID from props
        ...processedFormData,
        resume_id: selectedResumeId, // Include selected resume ID
        ...(otpCode && { otp_code: otpCode })
      }

      const response = await apiRequest('applicants/apply', {
        method: 'POST',
        body: JSON.stringify(requestData)
      })

      const data = await response.json()

      if (data.data?.status === 'otp_required') {
        setNeedsOtp(true)
        setOtpSent(true)
        setError('')
        // For testing, show the OTP code in console
        if (data.data.otp_code) {
          console.log('OTP Code for testing:', data.data.otp_code)
        }
      } else if (data.data?.status === 'invalid_otp') {
        setError('Invalid or expired OTP code. Please try again.')
      } else if (data.data?.status === 'duplicate') {
        // Show resume update option for duplicate applications
        if (data.data.application_id && authenticatedEmail) {
          setExistingApplicationId(data.data.application_id)
          setShowResumeUpdate(true)
          setError('')
        } else {
          // For non-logged-in users, show traditional application status
          if (data.data.application_id) {
            setExistingApplicationId(data.data.application_id)
            setShowApplicationStatus(true)
          } else {
            setError('You have already applied to this position.')
          }
        }
      } else if (response.ok && data.success) {
        setSuccess(true)
        setTimeout(() => {
          onSuccess()
        }, 2000)
      } else {
        // Handle validation errors from FastAPI
        if (data.detail && Array.isArray(data.detail)) {
          const validationErrors: Record<string, string> = {}
          data.detail.forEach((error: any) => {
            if (error.loc && error.loc.length >= 2) {
              const fieldName = error.loc[error.loc.length - 1] // Get the last part of the path
              validationErrors[fieldName] = error.msg
            }
          })
          
          if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors)
            setError('Please fix the validation errors below')
          } else {
            setError(data.message || data.error || 'Failed to submit application')
          }
        } else {
          setError(data.message || data.error || 'Failed to submit application')
        }
      }
    } catch (err: any) {
      setError('Failed to submit application. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (showResumeUpdate) {
    return (
      <div className="space-y-6">
        <div className="text-center py-4">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">You've Already Applied!</h3>
          <p className="text-blue-100 mb-4">
            You have already submitted an application for this position. 
            Would you like to update your resume?
          </p>
        </div>

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-100 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-2">
            Update Your Resume
          </label>
          <ResumeSelector
            selectedResumeId={selectedResumeId || undefined}
            onResumeSelect={handleResumeSelect}
            onError={(error) => setError(error)}
            className="mb-4"
            showMetadata={true}
          />
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleUpdateResume}
            disabled={isUpdatingResume || !selectedResumeId}
            className="flex-1 bg-white text-blue-600 hover:bg-blue-50 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            {isUpdatingResume ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Updating Resume...</span>
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                <span>Update Resume</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => {
              setShowResumeUpdate(false)
              setShowApplicationStatus(true)
            }}
            className="flex-1 bg-gray-600 text-white hover:bg-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
          >
            View Application Status
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="text-center py-6">
        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">
          {isUpdatingResume ? 'Resume Updated Successfully!' : 'Application Submitted!'}
        </h3>
        <p className="text-blue-100">
          {isUpdatingResume 
            ? 'Your resume has been updated for this position.'
            : 'Thank you for applying. We\'ll review your application and get back to you soon.'
          }
        </p>
      </div>
    )
  }

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Resume Upload Widget */}
      <div>
        <label className="block text-blue-100 text-sm font-medium mb-2">
          Quick Fill from Resume
        </label>
        <ResumeUploadWidget
          onDataParsed={handleResumeDataParsed}
          onError={handleResumeParseError}
          mode="full-parse"
          className="mb-4"
        />
        {resumeParseError && (
          <div className="flex items-center space-x-2 p-3 bg-yellow-100 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-yellow-700 text-sm">{resumeParseError}</span>
          </div>
        )}
      </div>
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-100 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {needsOtp && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="mb-3">
            <CheckCircle className="w-5 h-5 text-blue-500 inline mr-2" />
            <span className="text-blue-700 font-medium">Email Verification Required</span>
          </div>
          <p className="text-blue-600 text-sm mb-4">
            We've sent a 6-digit verification code to your email address. Please enter it below to complete your application.
          </p>
          <div>
            <label className="block text-blue-700 text-sm font-medium mb-2">
              Verification Code *
            </label>
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest"
              disabled={submitting}
              maxLength={6}
              style={{ letterSpacing: '0.5em' }}
            />
            <p className="text-xs text-blue-600 mt-1">
              Check your email for the verification code. It may take a few minutes to arrive.
            </p>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            First Name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="Your first name"
              className={`w-full pl-10 pr-4 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                shouldLockFirstName ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
              }`}
              disabled={submitting || shouldLockFirstName}
              readOnly={shouldLockFirstName}
              required
            />
            {userProfile && (
              <p className="text-xs text-green-200 mt-1 flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                From your profile
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Last Name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Your last name"
              className={`w-full pl-10 pr-4 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                shouldLockLastName ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
              }`}
              disabled={submitting || shouldLockLastName}
              readOnly={shouldLockLastName}
              required
            />
            {userProfile && (
              <p className="text-xs text-green-200 mt-1 flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                From your profile
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Email *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className={`w-full pl-10 pr-4 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${authenticatedEmail ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
              disabled={submitting || !!authenticatedEmail}
              readOnly={!!authenticatedEmail}
              required
            />
            {authenticatedEmail && (
              <p className="text-xs text-green-200 mt-1 flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                Email verified via OTP
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Phone
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Current Title
          </label>
          <input
            type="text"
            value={formData.current_title}
            onChange={(e) => handleInputChange('current_title', e.target.value)}
            placeholder="e.g., Software Engineer"
            className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Current Company
          </label>
          <input
            type="text"
            value={formData.current_company}
            onChange={(e) => handleInputChange('current_company', e.target.value)}
            placeholder="e.g., Tech Corp Inc."
            className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Current Location
          </label>
          <LocationAutocomplete
            value={formData.current_location}
            onChange={(value) => handleInputChange('current_location', value)}
            placeholder="Search for your current city..."
            disabled={submitting}
            className="text-gray-900"
          />
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Years of Experience
          </label>
          <input
            type="number"
            step="0.5"
            value={formData.total_experience_years}
            onChange={(e) => handleInputChange('total_experience_years', e.target.value)}
            placeholder="e.g., 3.5"
            className={`w-full px-4 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
              hasFieldError('total_experience_years') 
                ? 'border-red-500 focus:ring-red-500' 
                : 'border-gray-300'
            }`}
            disabled={submitting}
          />
          {hasFieldError('total_experience_years') && (
            <p className="text-red-300 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {getFieldError('total_experience_years')}
            </p>
          )}
        </div>
      </div>

      {/* Summary */}
      <div>
        <label className="block text-blue-100 text-sm font-medium mb-1">
          Professional Summary
        </label>
        <textarea
          value={formData.summary}
          onChange={(e) => handleInputChange('summary', e.target.value)}
          placeholder="Brief summary of your experience and skills..."
          rows={3}
          className="w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 resize-none"
          disabled={submitting}
        />
      </div>

      {/* Salary and Date */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Expected Salary
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="number"
              value={formData.expected_salary}
              onChange={(e) => handleInputChange('expected_salary', e.target.value)}
              placeholder="80000"
              className={`w-full pl-10 pr-4 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                hasFieldError('expected_salary') 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-300'
              }`}
              disabled={submitting}
            />
          </div>
          {hasFieldError('expected_salary') && (
            <p className="text-red-300 text-xs mt-1 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {getFieldError('expected_salary')}
            </p>
          )}
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Currency
          </label>
          <select
            value={formData.expected_salary_currency}
            onChange={(e) => handleInputChange('expected_salary_currency', e.target.value)}
            className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            disabled={submitting}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="INR">INR</option>
          </select>
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Available Start Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={formData.available_start_date}
              onChange={(e) => handleInputChange('available_start_date', e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      {/* Resume Selection */}
      <div>
        <label className="block text-blue-100 text-sm font-medium mb-2">
          Resume/CV *
        </label>
        <ResumeSelector
          selectedResumeId={selectedResumeId || undefined}
          onResumeSelect={handleResumeSelect}
          onError={(error) => setError(error)}
          className="mb-4"
          showMetadata={true}
        />
      </div>

      {/* Additional URLs */}
      <div className="space-y-3">

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            LinkedIn Profile
          </label>
          <input
            type="url"
            value={formData.linkedin_url}
            onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
            placeholder="https://linkedin.com/in/yourprofile"
            className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-blue-100 text-sm font-medium mb-1">
            Portfolio URL
          </label>
          <input
            type="url"
            value={formData.portfolio_url}
            onChange={(e) => handleInputChange('portfolio_url', e.target.value)}
            placeholder="https://yourportfolio.com"
            className="w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            disabled={submitting}
          />
        </div>
      </div>

      {/* Cover Letter */}
      <div>
        <label className="block text-blue-100 text-sm font-medium mb-1">
          Cover Letter (optional)
        </label>
        <textarea
          value={formData.cover_letter}
          onChange={(e) => handleInputChange('cover_letter', e.target.value)}
          placeholder="Tell us why you're interested in this position and what makes you a great fit..."
          rows={4}
          className="w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 resize-none"
          disabled={submitting}
        />
        <p className="text-xs text-blue-100 mt-1">
          {formData.cover_letter.length} / 1000 characters
        </p>
      </div>

      {/* Data Consent */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="data_consent"
          checked={formData.data_consent_given}
          onChange={(e) => handleInputChange('data_consent_given', e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
          disabled={submitting}
          required
        />
        <label htmlFor="data_consent" className="text-xs text-gray-700">
          I consent to the collection and processing of my personal data for recruitment purposes. 
          This information will be used to evaluate my application and may be shared with relevant stakeholders in the hiring process.
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={submitting || (needsOtp && otpCode.length !== 6)}
        className="w-full bg-white text-blue-600 hover:bg-blue-50 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
      >
        {submitting ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            <span>{needsOtp ? 'Verifying...' : 'Submitting...'}</span>
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            <span>{needsOtp ? 'Verify & Submit Application' : 'Submit Application'}</span>
          </>
        )}
      </button>
    </form>

    {/* Application Status Modal */}
    {showApplicationStatus && existingApplicationId && (
      <ApplicationStatus
        applicationId={existingApplicationId}
        positionTitle={position.title}
        companyName={position.company_name}
        onClose={() => {
          setShowApplicationStatus(false)
          setExistingApplicationId(null)
        }}
      />
    )}
  </>
  )
}
